import { useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CONFIG, tierName, type Round } from '@h2h/shared';
import { AppShell, Content } from '../components/ui/AppShell';
import { DesktopColumns } from '../components/ui/DesktopColumns';
import { Placeholder } from '../components/ui/Placeholder';
import { Avatar } from '../components/ui/bits';
import { TeamBadge } from '../components/TeamBadge';
import { ChevronLeft, Clock, Info, Swords } from '../components/ui/icons';
import { useHaptics } from '../hooks/useHaptics';
import { useOpenProfile } from '../hooks/useOpenProfile';
import { useAuth } from '../context/AuthContext';
import {
  useUser,
  useProfile,
  useMyMemberships,
  useMyDuels,
  useLeagueRounds,
  useRoundFixtures,
  useChallengeFriend
} from '../hooks/useQueries';
import { FOCUSED_LEAGUE_ID, isCyclingLeagueId } from '../data/leagues';
import { shortRider } from '../lib/cyclingRound';
import { cap, countdownTo, initials } from '../lib/format';
import { cn } from '../lib/cn';

function teamCode(name: string | undefined, code?: string): string {
  if (code) return code;
  if (!name) return '?';
  return name.replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase() || name.slice(0, 3).toUpperCase();
}

/** A round still open to new picks: OPEN, or SCHEDULED with published fixtures. */
function isChallengeable(r: Round): boolean {
  return (r.status === 'OPEN' || r.status === 'SCHEDULED') && (r.fixtureIds?.length ?? 0) > 0;
}

/** A consistent lock countdown for every round chip (no OPEN/SCHEDULED split — the
 *  lock instant is always the round's first kickoff). */
function lockLabel(r: Round, now: number): string {
  const c = countdownTo(r.firstKickoffAt, now);
  return c === 'now' ? 'Locking now' : `Locks in ${c}`;
}

/** Round labels read "Group Stage · Tue 16 Jun" — split the leading stage kicker
 *  from the date so the chip can stack them instead of wrapping mid-phrase. */
function splitRoundLabel(label: string): { kicker: string | null; main: string } {
  const parts = label.split('·').map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 2) return { kicker: parts[0], main: parts.slice(1).join(' · ') };
  return { kicker: null, main: label };
}

export default function ChallengeSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { impact } = useHaptics();
  const openProfile = useOpenProfile();
  const { opponentId } = useParams<{ opponentId: string }>();
  const { uid } = useAuth();

  const { data: opponent, isLoading } = useUser(opponentId);
  const { data: me } = useProfile(uid);
  const { data: myMemberships } = useMyMemberships(uid);
  const { data: theirMemberships } = useMyMemberships(opponentId ?? null);
  const { data: myDuels } = useMyDuels(uid);

  // The shared world: a league both have joined — prefer the focused competition.
  // Duels work for every sport (a football round compares market picks, a cycling
  // stage compares binary duel calls), so any shared league can anchor a duel.
  const leagueId = useMemo(() => {
    const preferred = (location.state as { leagueId?: string } | null)?.leagueId;
    const mine = new Set((myMemberships ?? []).map((m) => m.leagueId));
    const shared = (theirMemberships ?? []).filter((m) => mine.has(m.leagueId)).map((m) => m.leagueId);
    if (preferred && shared.includes(preferred)) return preferred;
    return shared.includes(FOCUSED_LEAGUE_ID) ? FOCUSED_LEAGUE_ID : shared[0];
  }, [myMemberships, theirMemberships, location.state]);
  const isCycling = !!leagueId && isCyclingLeagueId(leagueId);

  const { data: rounds } = useLeagueRounds(leagueId);
  const eligibleRounds = useMemo(
    () => (rounds ?? []).filter(isChallengeable).sort((a, b) => a.firstKickoffAt - b.firstKickoffAt),
    [rounds]
  );

  const [picked, setPicked] = useState<string | null>(null);
  const selectedRoundId = picked ?? eligibleRounds[0]?.roundId ?? null;
  const selectedRound = eligibleRounds.find((r) => r.roundId === selectedRoundId) ?? null;

  const { data: fixtures } = useRoundFixtures(leagueId, selectedRoundId);
  // Football fixtures order by kickoff; cycling duels share the stage depart, so a
  // stable id order keeps the slate consistent with the picks screen.
  const orderedFixtures = useMemo(
    () =>
      [...(fixtures ?? [])].sort((a, b) =>
        isCycling ? a.fixtureId.localeCompare(b.fixtureId) : a.kickoffAt - b.kickoffAt
      ),
    [fixtures, isCycling]
  );

  const challenge = useChallengeFriend(uid);

  // Resolved series vs this player, scoped to the league this duel will be played
  // in (matches the HeadToHead duel view's league-scoped series).
  const series = (myDuels ?? []).filter(
    (d) => d.status === 'RESOLVED' && d.leagueId === leagueId && d.participantIds.includes(opponentId ?? '')
  );
  const myWins = series.filter((d) => d.winnerId === uid).length;
  const theirWins = series.filter((d) => d.winnerId === opponentId).length;

  const myTier = (myMemberships ?? []).find((m) => m.leagueId === leagueId)?.tier;
  const theirTier = (theirMemberships ?? []).find((m) => m.leagueId === leagueId)?.tier;

  if (isLoading || !opponent) {
    return (
      <AppShell>
        <Content>
          <NavBar title="Back" onBack={() => navigate(-1)} />
          <Placeholder
            Icon={Swords}
            title={isLoading ? 'Loading…' : 'Player not found'}
            body="This player isn’t available to challenge."
          />
        </Content>
      </AppShell>
    );
  }

  const name = opponent.displayName ?? 'Player';

  if (!leagueId) {
    return (
      <AppShell>
        <Content>
          <NavBar title={name} onBack={() => navigate(-1)} />
          <Placeholder
            Icon={Swords}
            title="Not in the same league"
            body={`You and ${name} aren’t in the same league yet. Join the same competition to duel.`}
          />
        </Content>
      </AppShell>
    );
  }

  const send = () => {
    if (!selectedRoundId || !opponentId) return;
    impact('medium');
    challenge.mutate(
      { leagueId, opponentId, roundId: selectedRoundId },
      { onSuccess: (res) => navigate(`/duel/${res.duel.duelId}`) }
    );
  };

  return (
    <AppShell>
      <Content width="wide">
        <NavBar title={name} onBack={() => navigate(-1)} />

        <div className="pt-3">
          <h1 className="title text-[28px] md:text-[34px]">New challenge</h1>
          <p className="mt-[6px] text-[13px] font-medium text-ink2">
            {isCycling
              ? 'Pick a stage — you’ll both call the same duels.'
              : 'Pick a round — you’ll both predict the same fixtures.'}
          </p>
        </div>

        <DesktopColumns
          railFirst
          asidePosition="left"
          asideWidth="380px"
          aside={
            <>
        {/* VS hero */}
        <div className="relative mt-4 overflow-hidden rounded-[28px] bg-gradient-to-br from-[#1C1C24] to-[#0A0A0E] p-5 shadow-[0_22px_42px_-18px_rgba(0,0,0,0.55)]">
          <span className="pointer-events-none absolute -left-[30px] -top-[50px] h-[180px] w-[180px] rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.18),transparent_70%)]" />
          <span className="pointer-events-none absolute -right-[30px] -top-[50px] h-[180px] w-[180px] rounded-full bg-[radial-gradient(circle,rgba(91,110,225,0.24),transparent_70%)]" />
          <div className="relative flex items-center justify-between">
            <Combatant name="You" tone={me?.avatarTone ?? 0} you tier={myTier} onClick={() => openProfile(undefined, true)} />
            <div className="grid h-[40px] w-[40px] rotate-45 place-items-center rounded-[12px] border border-white/15 bg-white/[0.08]">
              <span className="-rotate-45 text-[12px] font-extrabold text-white">VS</span>
            </div>
            <Combatant name={name} tone={opponent.avatarTone ?? 0} tier={theirTier} onClick={() => openProfile(opponentId)} />
          </div>
          {(myWins > 0 || theirWins > 0) && (
            <div className="relative mt-4 flex items-center justify-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.08] px-3 py-1 text-[11px] font-semibold text-[#9A9AA4]">
                Series
                <span className="num font-extrabold text-white">{myWins}–{theirWins}</span>
                <span className="text-white/30">·</span>
                {myWins === theirWins ? 'all square' : myWins > theirWins ? 'you lead' : `${name.split(/\s+/)[0]} leads`}
              </span>
            </div>
          )}
        </div>
            </>
          }
          main={
            <>
        {/* round selector */}
        {eligibleRounds.length === 0 ? (
          <div className="mt-5 rounded-2xl border border-dashed border-ink4 bg-wash px-4 py-6 text-center text-[14px] font-medium text-ink2">
            No upcoming rounds are open for a duel right now. Check back when the next round opens.
          </div>
        ) : (
          <>
            <SectionLabel
              className="pt-7"
              title={isCycling ? 'Pick a stage' : 'Pick a round'}
              meta={eligibleRounds.length > 1 ? 'Swipe for more' : undefined}
            />
            <div className="native-scroll -mx-safe flex gap-[10px] overflow-x-auto px-safe pb-1 no-scrollbar md:mx-0 md:px-0">
              {eligibleRounds.map((r) => {
                const active = r.roundId === selectedRoundId;
                const { kicker, main } = splitRoundLabel(r.label);
                return (
                  <button
                    key={r.roundId}
                    type="button"
                    onClick={() => {
                      impact('light');
                      setPicked(r.roundId);
                    }}
                    className={cn(
                      'relative w-[140px] shrink-0 rounded-[20px] px-4 py-[15px] text-left transition-[transform,background-color,border-color,box-shadow] duration-150 ease-ios-out',
                      active
                        ? 'bg-night shadow-[0_14px_28px_-14px_rgba(0,0,0,0.5)]'
                        : 'border border-black/[0.06] bg-card active:scale-[0.98]'
                    )}
                  >
                    {active && (
                      <span className="absolute right-3 top-3 h-[7px] w-[7px] rounded-full bg-accent shadow-[0_0_0_3px_rgba(36,197,106,0.25)]" />
                    )}
                    {kicker && (
                      <div
                        className={cn(
                          'text-[10px] font-bold uppercase tracking-[0.06em]',
                          active ? 'text-white/55' : 'text-ink3'
                        )}
                      >
                        {kicker}
                      </div>
                    )}
                    <div
                      className={cn(
                        'mt-1 text-[16px] font-extrabold leading-[1.15] tracking-snug',
                        active ? 'text-white' : 'text-ink'
                      )}
                    >
                      {main}
                    </div>
                    <div
                      className={cn(
                        'mt-[10px] inline-flex items-center gap-[5px] text-[11px] font-semibold',
                        active ? 'text-[#9A9AA4]' : 'text-ink2'
                      )}
                    >
                      <Clock width={12} height={12} strokeWidth={2.4} />
                      {lockLabel(r, Date.now())}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* fixtures / duels preview */}
            <SectionLabel
              className="pt-7"
              title={isCycling ? 'The duels' : 'The fixtures'}
              meta="Same for both players"
            />
            <div className="flex flex-col gap-[8px]">
              {orderedFixtures.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-ink4 bg-wash px-4 py-5 text-center text-[13px] font-medium text-ink2">
                  {isCycling ? 'Duels drop once the startlist is published.' : 'Fixtures load as soon as they’re published.'}
                </div>
              ) : isCycling ? (
                orderedFixtures.map((f) => (
                  <div
                    key={f.fixtureId}
                    className="flex items-center gap-3 rounded-[18px] border border-black/5 bg-card px-4 py-[13px] shadow-tile"
                  >
                    <div className="flex min-w-0 flex-1 items-center justify-center gap-2.5">
                      <span className="flex-1 truncate text-right text-[13.5px] font-extrabold text-ink">{shortRider(f.riderA?.riderName ?? f.homeTeam)}</span>
                      <span className="text-[10px] font-bold text-ink4">v</span>
                      <span className="flex-1 truncate text-[13.5px] font-extrabold text-ink">{shortRider(f.riderB?.riderName ?? f.awayTeam)}</span>
                    </div>
                  </div>
                ))
              ) : (
                orderedFixtures.map((f) => (
                  <div
                    key={f.fixtureId}
                    className="flex items-center gap-3 rounded-[18px] border border-black/5 bg-card px-3.5 py-[12px] shadow-tile"
                  >
                    <KickoffChip ms={f.kickoffAt} />
                    <span className="h-9 w-px shrink-0 bg-black/[0.06]" />
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="flex-1 truncate text-right text-[13.5px] font-extrabold text-ink">{f.homeTeam}</span>
                      <TeamBadge code={teamCode(f.homeTeam, f.homeCode)} logo={f.homeLogo} name={f.homeTeam} sizeClass="h-[28px] w-[28px]" roundedClass="rounded-[8px]" textClass="text-[9px]" />
                      <span className="px-0.5 text-[10px] font-bold text-ink4">v</span>
                      <TeamBadge code={teamCode(f.awayTeam, f.awayCode)} logo={f.awayLogo} name={f.awayTeam} sizeClass="h-[28px] w-[28px]" roundedClass="rounded-[8px]" textClass="text-[9px]" />
                      <span className="flex-1 truncate text-[13.5px] font-extrabold text-ink">{f.awayTeam}</span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* format explainer */}
            <div className="mt-4 flex items-start gap-[10px] rounded-[18px] border border-black/5 bg-wash px-4 py-[13px]">
              <Info width={15} height={15} strokeWidth={2.2} className="mt-[2px] shrink-0 text-ink3" />
              <span className="text-[12px] font-medium leading-[1.45] text-ink2">
                {isCycling ? (
                  <>
                    You’ll each call all{' '}
                    <span className="font-bold text-ink">{selectedRound?.picksRequired ?? CONFIG.CYCLING.DUELS_PER_STAGE}</span> duels.
                    Whoever scores more across their duels wins — picks stay hidden until the stage starts.
                  </>
                ) : (
                  <>
                    You’ll each build cards on any number of this round’s fixtures. Whoever scores more across
                    their cards wins the duel — picks stay hidden until kickoff.
                  </>
                )}
              </span>
            </div>

            {challenge.isError && (
              <div className="mt-3 rounded-xl bg-miss/10 px-3 py-2 text-[13px] font-semibold text-danger">
                Couldn’t send the challenge. Please try again.
              </div>
            )}

            <button
              type="button"
              disabled={!selectedRoundId || challenge.isPending}
              onClick={send}
              className="tap mt-5 flex w-full items-center justify-center gap-[8px] rounded-[16px] bg-accent py-[16px] text-[16px] font-extrabold text-white shadow-[0_16px_30px_-14px_rgba(10,150,110,0.7)] disabled:opacity-50"
            >
              <Swords width={17} height={17} strokeWidth={2} />
              {challenge.isPending ? 'Sending…' : `Send challenge to ${name.split(/\s+/)[0]}`}
            </button>
          </>
        )}
            </>
          }
        />
      </Content>
    </AppShell>
  );
}

/** A consistent section header: a bold label on the left, optional meta on the right. */
function SectionLabel({ title, meta, className }: { title: string; meta?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-baseline justify-between pb-2.5', className)}>
      <span className="text-[13px] font-extrabold tracking-snug text-ink">{title}</span>
      {meta != null && <span className="text-[11px] font-semibold text-ink3">{meta}</span>}
    </div>
  );
}

/** A compact two-line kickoff stamp (day over time) used as a fixture row's left rail. */
function KickoffChip({ ms }: { ms: number }) {
  const d = new Date(ms);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return (
    <div className="flex w-[42px] shrink-0 flex-col items-center leading-tight">
      <span className="text-[10px] font-bold uppercase tracking-[0.04em] text-ink3">{day}</span>
      <span className="num text-[13px] font-extrabold text-ink">{time}</span>
    </div>
  );
}

function NavBar({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="flex items-center justify-between pt-2 md:pt-5">
      <button type="button" onClick={onBack} className="tap -ml-1 flex items-center gap-[2px] text-ink">
        <ChevronLeft width={22} height={22} strokeWidth={2.4} />
        <span className="text-[16px] font-semibold tracking-snug">{title}</span>
      </button>
    </div>
  );
}

function Combatant({
  name,
  tone,
  you,
  tier,
  onClick
}: {
  name: string;
  tone: number;
  you?: boolean;
  tier?: number;
  onClick?: () => void;
}) {
  const inner = (
    <>
      {you ? (
        <div className="grid h-[56px] w-[56px] place-items-center rounded-full border-2 border-white/15 bg-gradient-to-br from-night2 to-night text-[20px] font-extrabold text-white shadow-mark">
          {initials(name)}
        </div>
      ) : (
        <Avatar name={name} tone={tone} size={56} className="border-2 border-white/15" />
      )}
      <span className="text-[12px] font-extrabold tracking-snug text-white">{you ? 'You' : name}</span>
      {tier != null && (
        <span className="rounded-[5px] bg-gradient-to-br from-gold-top to-gold-bottom px-[7px] py-[2px] text-[9px] font-extrabold tracking-wide text-gold-ink">
          {cap(tierName(tier)).toUpperCase()}
        </span>
      )}
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="tap flex flex-1 flex-col items-center gap-2">
        {inner}
      </button>
    );
  }
  return <div className="flex flex-1 flex-col items-center gap-2">{inner}</div>;
}
